import { useState, useEffect, useRef, useContext } from "react";
import Button from "react-bootstrap/Button";
import { useDevices } from "@daily-co/daily-react";
// import { ReactMic } from "react-mic";
import { Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import spinningCards from "../../assets/images/waitingroom/playing-cards-spinning-loader.svg";
import "./VideoSettings.css";
import CustomSelect from "../CustomSelect/CustomSelect";
import UserContext from "../../context/UserContext";
import Microphone from "./Microphone";
import testSound from "../../assets/sounds/speaker-test.mp3";
import Error from "../toast/erros";
import Layout from "../layout/layout";
import intSignConvert from "../../utils/intSignConvert";

let stream;

function VideoSettings() {
  const InitialState = {
    mic: localStorage.getItem("mic")
      ? JSON.parse(localStorage.getItem("mic"))
      : "",
    speaker: localStorage.getItem("speaker")
      ? JSON.parse(localStorage.getItem("speaker"))
      : "",
    camera: localStorage.getItem("camera")
      ? JSON.parse(localStorage.getItem("camera"))
      : "",
  };

  const speakerTestAudio = new Audio(testSound);
  const { user } = useContext(UserContext);
  const { setUserMic, setUserCamera, setUserSpeaker, responsiveScale } = useContext(UserContext);
  const { scaleValue, topValue } = responsiveScale;
  const fetchMediaDevice = useDevices();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef();
  const [micOption, setMicOption] = useState([]);
  const [speakerOption, setSpeakerOption] = useState([]);
  const [cameraOption, setCameraOption] = useState([]);
  //  const [values, setValues] = useState(InitialState);
  const [videoActive, setVideoActive] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [values, setValues] = useState(InitialState);
  const [message, setMessage] = useState("");
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [startRecord, setStartRecord] = useState(false);
  const { gameId } = useParams();

  const joinWaitingRoom = () => {
    window.location.href = `/user/waiting-room/${gameId}`;
  };

  // Query a single permission in a try...catch block and return result
  const getPermission = async (permission) => {
    try {
      const result = await navigator.permissions.query({ name: permission });
      if (result.state === "denied" || result.state === "prompt") {
        return false;
      }
      return true;
    } catch (error) {
      return `${permission} (not supported)`;
    }
  };
  const startVideo = async () => {
    if (!values.camera) {
      setMessage("Please select a camera first");
      return setTimeout(() => {
        setMessage("");
      }, 2000);
    }
    setIsVideoPlaying(true);
    setVideoActive(true);
    if (!navigator?.mediaDevices?.getUserMedia) {
      setMessage("User media not supported");
      return setTimeout(() => {
        setMessage("");
      }, 2000);
    }
    if (stream && Object.keys(stream).length) {
      stream?.getTracks().forEach((track) => track.stop());
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          aspectRatio: 16 / 9,
          deviceId: values.camera.value,
        },
      });

      videoRef.current.srcObject = stream;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`${error.name}: ${error.message}`);
    }
  };
  const getMediaDevices = async () => {
    try {
      // Check if either the current camera or current microphone is not selected
      if (!fetchMediaDevice?.currentCam || !fetchMediaDevice?.currentMic) {
        // Request video only if not a spectator
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            aspectRatio: 16 / 9,
            deviceId: values.camera.value,
          },
          audio: true,
        });
      }
      setShowPermissionModal(false);

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoCameras = allDevices.filter(
        (device) => device.kind === "videoinput"
      );
      const microphones = allDevices.filter(
        (device) => device.kind === "audioinput"
      );
      const speakers = allDevices.filter(
        (device) => device.kind === "audiooutput"
      );

      const setOptions = (type, devices) => {
        const options = devices.map(({ label, deviceId }) => ({
          label,
          value: deviceId,
        }));
        switch (type) {
          case "camera":
            setCameraOption(options);
            break;
          case "mic":
            setMicOption(options);
            break;
          case "speaker":
            setSpeakerOption(options);
            break;
          default:
            break;
        }
      };

      const setDefaultDevice = (type, devices) => {
        if (!localStorage.getItem(type)) {
          const defaultDevice = devices.length > 0 ? devices[0] : null;
          localStorage.setItem(type, JSON.stringify(defaultDevice));
        }
      };

      // Set options for UI
      setOptions("camera", videoCameras);
      setOptions("mic", microphones);
      setOptions("speaker", speakers);

      // Set default devices in local storage
      setDefaultDevice("camera", videoCameras);
      setDefaultDevice("mic", microphones);
      setDefaultDevice("speaker", speakers);

      // Set initial values for the UI based on local storage or default devices
      setValues((prevValues) => ({
        ...prevValues,
        camera:
          JSON.parse(localStorage.getItem("camera")) || videoCameras[0] || null,
        speaker:
          JSON.parse(localStorage.getItem("speaker")) || speakers[0] || null,
        mic: JSON.parse(localStorage.getItem("mic")) || microphones[0] || null,
      }));
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error accessing camera and microphone:", error);
      setMessage(`${error.name}: ${error.message}`);
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  };
  const checkHasPermission = async () => {
    const microphonePermission = await getPermission("microphone");
    const cameraPermission = await getPermission("camera");
    if (!microphonePermission || !cameraPermission) {
      setShowPermissionModal(true);
    } else {
      getMediaDevices();
      const isCameraOn = localStorage.getItem("isCameraOn", false);
      const isMicOn = localStorage.getItem("isMicOn", false);
      if (isCameraOn === "true") {
        startVideo();
      }
      if (isMicOn === "true") {
        setMicActive(!micActive);
      }
    }
  };
  const customMicOption = {
    icon: <MicFilledActiveIcon />,
    label: "Test Mic",
    component: <Microphone />,
  };
  const customSpeakerOption = {
    icon: <SpeakerFilledIcon />,
    label: "Test Speaker",
    component: "",
  };

  const handleMicTest = () => {
    setStartRecord(!startRecord);
  };

  const handleChange = (value, type) => {
    setValues({ ...values, [type]: value });
    if (type === "mic") {
      setUserMic(value);
    } else if (type === "camera") {
      setIsVideoPlaying(false);
      setUserCamera(value);
      setVideoActive(false);
    } else if (type === "speaker") {
      setUserSpeaker(value);
    }
    if (["mic", "camera", "speaker"].includes(type)) {
      localStorage.setItem(type, JSON.stringify(value));
    } else {
      localStorage.setItem(type, value);
    }
  };

  const handleVideo = () => {
    const isStartVideo = !videoActive;
    if (isStartVideo) {
      localStorage.setItem("isCameraOn", true);
      startVideo();
    } else {
      localStorage.setItem("isCameraOn", false);
      setIsVideoPlaying(false);
      setVideoActive(!videoActive);
    }
  };

  const handleMic = () => {
    setMicActive(!micActive);
  };

  const handlePermissionClose = () => {
    setShowPermissionModal(false);
  };

  const handleSpeakerTest = () => {
    speakerTestAudio.play();
  };

  useEffect(() => {
    checkHasPermission();

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [fetchMediaDevice?.currentCam, fetchMediaDevice?.currentMic]);

  return (
    <Layout>
      <Helmet>
        <body className="video-setting-popup" />
      </Helmet>
      <div
        className="video-setting-view"
        style={{
          transform: `translate(-50%, ${intSignConvert(topValue)}%) scale(${
            scaleValue / 100
          })`,
        }}
      >
        <div className="video-container">
          <div className="video-wrapper">
            <div className="video-section">
              {micActive && (
                <div className="mic-badge">
                  <MicActiveBadge />
                </div>
              )}
              {isVideoPlaying ? (
                <video
                  className="video-view"
                  ref={videoRef}
                  playsInline
                  autoPlay
                  controls={false}
                  muted
                />
              ) : (
                <div className="default-placeholder">
                  <h2>Camera is off</h2>
                </div>
              )}
              <div className="video-footer">
                <div className="footer-action-container">
                  <div className="video-footer-name">{user?.name}</div>
                  <div className="video-mic-action">
                    <span
                      className={micActive ? "mic active" : "mic"}
                      onClick={handleMic}
                      role="presentation"
                    >
                      {micActive ? <MicActiveIcon /> : <MicDisableIcon />}
                    </span>
                    <span
                      className={videoActive ? "active" : ""}
                      onClick={handleVideo}
                      role="presentation"
                    >
                      {videoActive ? <VideoIcon /> : <CameraDisableIcon />}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="video-action-container">
              <CustomSelect
                key={1}
                options={micOption}
                customOption={customMicOption}
                value={
                  JSON.parse(localStorage.getItem("mic"))
                    ? JSON.parse(localStorage.getItem("mic"))
                    : values?.mic
                      ? values?.mic
                      : micOption[0]
                }
                onChange={(value) => handleChange(value, "mic")}
                label="Choose an option"
                activeIcon={<ActiveTickIcon />}
                buttonIcon={<MicActiveIcon />}
                handleTestIcon={handleMicTest}
              />
              <CustomSelect
                key={2}
                options={speakerOption}
                customOption={customSpeakerOption}
                value={
                  JSON.parse(localStorage.getItem("speaker"))
                    ? JSON.parse(localStorage.getItem("speaker"))
                    : values?.speaker
                      ? values?.speaker
                      : speakerOption[0]
                }
                onChange={(value) => handleChange(value, "speaker")}
                label="Choose an option"
                activeIcon={<ActiveTickIcon />}
                buttonIcon={<SpeakerIcon />}
                handleTestIcon={handleSpeakerTest}
              />
              <CustomSelect
                key={3}
                options={cameraOption}
                //  customOption={customCameraOption}
                value={
                  JSON.parse(localStorage.getItem("camera"))
                    ? JSON.parse(localStorage.getItem("camera"))
                    : values?.camera
                      ? values?.camera
                      : cameraOption[0]
                }
                onChange={(value) => handleChange(value, "camera")}
                label="Choose an option"
                activeIcon={<ActiveTickIcon />}
                buttonIcon={<CameraIcon />}
                handleTestIcon={() => {}}
              />
            </div>
          </div>
          <div className="ready-container">
            <div className="ready-to-play">
              <h2>Ready to play?</h2>
              <span>Make sure everything is set up correctly</span>
              <div className="signup-btn join-btn">
                <Button onClick={joinWaitingRoom}>Join the waiting room</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showPermissionModal}
        onHide={handlePermissionClose}
        centered
        className="poker-popup permission-popup"
      >
        <Modal.Body className="permission-popup-area">
          <div className="permission-cover">
            <img src={spinningCards} alt="" />
          </div>
          <h2>
            App needs microphone and camera access so you can interact with the
            other players
          </h2>
          <div className="signup-btn allow-camera-btn">
            <Button onClick={getMediaDevices}>
              Allow microphone and camera
            </Button>
          </div>
          <span
            role="presentation"
            onClick={() => setShowPermissionModal(false)}
          >
            Continue without microphone and camera
          </span>
        </Modal.Body>
      </Modal>
      {message ? <Error message={message} /> : ""}
    </Layout>
  );
}

export default VideoSettings;

function MicDisableIcon() {
  return (
    <svg
      width="33"
      height="32"
      viewBox="0 0 33 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.5 25V30"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 6L26.5 28"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22.04 23.0938C20.7109 24.1319 19.1169 24.7757 17.4396 24.9517C15.7624 25.1278 14.0694 24.8291 12.5537 24.0896C11.038 23.3501 9.76058 22.1996 8.86705 20.7693C7.97351 19.339 7.49983 17.6865 7.5 16"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M25.4991 16C25.5012 17.3694 25.1895 18.7211 24.5879 19.9513"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.335 20.125C18.5011 20.6984 17.512 21.0037 16.5 21C15.1739 21 13.9021 20.4732 12.9645 19.5355C12.0268 18.5979 11.5 17.3261 11.5 16V11.5"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.9121 6.0075C12.3005 5.11366 12.9416 4.35281 13.7566 3.81849C14.5716 3.28417 15.525 2.99968 16.4996 3C17.8257 3 19.0975 3.52678 20.0351 4.46447C20.9728 5.40215 21.4996 6.67392 21.4996 8V16C21.4999 16.175 21.4907 16.3498 21.4721 16.5238"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg
      width="33"
      height="32"
      viewBox="0 0 33 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24.5 8H4.5C3.94772 8 3.5 8.44772 3.5 9V23C3.5 23.5523 3.94772 24 4.5 24H24.5C25.0523 24 25.5 23.5523 25.5 23V9C25.5 8.44772 25.0523 8 24.5 8Z"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M25.5 14L31.5 10V22L25.5 18"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MicActiveBadge() {
  return (
    <svg
      width="16"
      height="4"
      viewBox="0 0 16 4"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.96875 2C9.96875 2.38938 9.85329 2.77002 9.63696 3.09378C9.42063 3.41754 9.11315 3.66988 8.75341 3.81889C8.39367 3.9679 7.99782 4.00689 7.61592 3.93092C7.23402 3.85496 6.88322 3.66745 6.60788 3.39212C6.33255 3.11678 6.14504 2.76598 6.06908 2.38408C5.99312 2.00218 6.0321 1.60633 6.18111 1.24659C6.33012 0.88685 6.58246 0.579373 6.90622 0.363044C7.22998 0.146715 7.61062 0.03125 8 0.03125C8.52215 0.03125 9.02291 0.238671 9.39212 0.607884C9.76133 0.977096 9.96875 1.47786 9.96875 2ZM2.375 0.03125C1.98562 0.03125 1.60498 0.146715 1.28122 0.363044C0.957463 0.579373 0.705123 0.88685 0.556113 1.24659C0.407103 1.60633 0.368115 2.00218 0.44408 2.38408C0.520045 2.76598 0.70755 3.11678 0.982884 3.39212C1.25822 3.66745 1.60902 3.85496 1.99092 3.93092C2.37282 4.00689 2.76867 3.9679 3.12841 3.81889C3.48815 3.66988 3.79563 3.41754 4.01196 3.09378C4.22829 2.77002 4.34375 2.38938 4.34375 2C4.34375 1.47786 4.13633 0.977096 3.76712 0.607884C3.3979 0.238671 2.89715 0.03125 2.375 0.03125ZM13.625 0.03125C13.2356 0.03125 12.855 0.146715 12.5312 0.363044C12.2075 0.579373 11.9551 0.88685 11.8061 1.24659C11.6571 1.60633 11.6181 2.00218 11.6941 2.38408C11.77 2.76598 11.9575 3.11678 12.2329 3.39212C12.5082 3.66745 12.859 3.85496 13.2409 3.93092C13.6228 4.00689 14.0187 3.9679 14.3784 3.81889C14.7382 3.66988 15.0456 3.41754 15.262 3.09378C15.4783 2.77002 15.5938 2.38938 15.5938 2C15.5938 1.74146 15.5428 1.48545 15.4439 1.24659C15.3449 1.00773 15.1999 0.790699 15.0171 0.607884C14.8343 0.425068 14.6173 0.280051 14.3784 0.181112C14.1395 0.082173 13.8835 0.03125 13.625 0.03125Z"
        fill="white"
      />
    </svg>
  );
}

function ActiveTickIcon() {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2.75C10.0716 2.75 8.18657 3.32183 6.58319 4.39317C4.97982 5.46451 3.73013 6.98726 2.99218 8.76884C2.25422 10.5504 2.06114 12.5108 2.43735 14.4021C2.81355 16.2934 3.74215 18.0307 5.10571 19.3943C6.46928 20.7579 8.20656 21.6865 10.0979 22.0627C11.9892 22.4389 13.9496 22.2458 15.7312 21.5078C17.5127 20.7699 19.0355 19.5202 20.1068 17.9168C21.1782 16.3134 21.75 14.4284 21.75 12.5C21.7473 9.91498 20.7192 7.43661 18.8913 5.60872C17.0634 3.78084 14.585 2.75273 12 2.75ZM16.2806 10.7806L11.0306 16.0306C10.961 16.1004 10.8783 16.1557 10.7872 16.1934C10.6962 16.2312 10.5986 16.2506 10.5 16.2506C10.4014 16.2506 10.3038 16.2312 10.2128 16.1934C10.1218 16.1557 10.039 16.1004 9.96938 16.0306L7.71938 13.7806C7.57865 13.6399 7.49959 13.449 7.49959 13.25C7.49959 13.051 7.57865 12.8601 7.71938 12.7194C7.86011 12.5786 8.05098 12.4996 8.25 12.4996C8.44903 12.4996 8.6399 12.5786 8.78063 12.7194L10.5 14.4397L15.2194 9.71937C15.2891 9.64969 15.3718 9.59442 15.4628 9.5567C15.5539 9.51899 15.6515 9.49958 15.75 9.49958C15.8486 9.49958 15.9461 9.51899 16.0372 9.5567C16.1282 9.59442 16.2109 9.64969 16.2806 9.71937C16.3503 9.78906 16.4056 9.87178 16.4433 9.96283C16.481 10.0539 16.5004 10.1515 16.5004 10.25C16.5004 10.3485 16.481 10.4461 16.4433 10.5372C16.4056 10.6282 16.3503 10.7109 16.2806 10.7806Z"
        fill="#C7AD7A"
      />
    </svg>
  );
}

function MicActiveIcon() {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.625 5C13.625 3.27411 12.2259 1.875 10.5 1.875C8.77411 1.875 7.375 3.27411 7.375 5V10C7.375 11.7259 8.77411 13.125 10.5 13.125C12.2259 13.125 13.625 11.7259 13.625 10V5Z"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5 15.625V18.75"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.125 10C16.125 11.4918 15.5324 12.9226 14.4775 13.9775C13.4226 15.0324 11.9918 15.625 10.5 15.625C9.00816 15.625 7.57742 15.0324 6.52252 13.9775C5.46763 12.9226 4.875 11.4918 4.875 10"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MicFilledActiveIcon() {
  return (
    <svg
      width="16"
      height="23"
      viewBox="0 0 16 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.5 11V5C3.5 3.80653 3.97411 2.66193 4.81802 1.81802C5.66193 0.974106 6.80653 0.5 8 0.5C9.19347 0.5 10.3381 0.974106 11.182 1.81802C12.0259 2.66193 12.5 3.80653 12.5 5V11C12.5 12.1935 12.0259 13.3381 11.182 14.182C10.3381 15.0259 9.19347 15.5 8 15.5C6.80653 15.5 5.66193 15.0259 4.81802 14.182C3.97411 13.3381 3.5 12.1935 3.5 11ZM15.5 11C15.5 10.8011 15.421 10.6103 15.2803 10.4697C15.1397 10.329 14.9489 10.25 14.75 10.25C14.5511 10.25 14.3603 10.329 14.2197 10.4697C14.079 10.6103 14 10.8011 14 11C14 12.5913 13.3679 14.1174 12.2426 15.2426C11.1174 16.3679 9.5913 17 8 17C6.4087 17 4.88258 16.3679 3.75736 15.2426C2.63214 14.1174 2 12.5913 2 11C2 10.8011 1.92098 10.6103 1.78033 10.4697C1.63968 10.329 1.44891 10.25 1.25 10.25C1.05109 10.25 0.860322 10.329 0.71967 10.4697C0.579018 10.6103 0.5 10.8011 0.5 11C0.502284 12.8586 1.19368 14.6504 2.44048 16.0288C3.68728 17.4072 5.40091 18.2743 7.25 18.4625V21.5C7.25 21.6989 7.32902 21.8897 7.46967 22.0303C7.61032 22.171 7.80109 22.25 8 22.25C8.19891 22.25 8.38968 22.171 8.53033 22.0303C8.67098 21.8897 8.75 21.6989 8.75 21.5V18.4625C10.5991 18.2743 12.3127 17.4072 13.5595 16.0288C14.8063 14.6504 15.4977 12.8586 15.5 11Z"
        fill="#C7AD7A"
      />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.75 13.125H3C2.83424 13.125 2.67527 13.0592 2.55806 12.9419C2.44085 12.8247 2.375 12.6658 2.375 12.5V7.5C2.375 7.33424 2.44085 7.17527 2.55806 7.05806C2.67527 6.94085 2.83424 6.875 3 6.875H6.75L12.375 2.5V17.5L6.75 13.125Z"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.75 6.875V13.125"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.5 8.34766C15.9021 8.80418 16.1239 9.39166 16.1239 10C16.1239 10.6083 15.9021 11.1958 15.5 11.6523"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.8184 6.25C18.7408 7.28129 19.2507 8.61638 19.2507 10C19.2507 11.3836 18.7408 12.7187 17.8184 13.75"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpeakerFilledIcon() {
  return (
    <svg
      width="23"
      height="20"
      viewBox="0 0 23 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 1.02344V18.9709C14.0023 19.1052 13.9697 19.2379 13.9055 19.3558C13.8412 19.4738 13.7475 19.5731 13.6334 19.6441C13.5038 19.7212 13.354 19.7574 13.2035 19.748C13.0529 19.7386 12.9087 19.6841 12.7897 19.5916L6.6425 14.8103C6.59793 14.7751 6.56195 14.7302 6.53726 14.6791C6.51258 14.6279 6.49984 14.5718 6.5 14.515V5.485C6.50017 5.42797 6.51335 5.37172 6.53853 5.32055C6.56371 5.26937 6.60023 5.22462 6.64531 5.18969L12.7925 0.408436C12.9272 0.304046 13.0935 0.248651 13.2639 0.251378C13.4343 0.254104 13.5987 0.314789 13.73 0.423436C13.8162 0.49753 13.8851 0.589686 13.9318 0.693375C13.9785 0.797064 14.0017 0.909746 14 1.02344ZM4.625 5.5H2C1.60218 5.5 1.22064 5.65803 0.93934 5.93934C0.658035 6.22064 0.5 6.60217 0.5 7V13C0.5 13.3978 0.658035 13.7794 0.93934 14.0607C1.22064 14.342 1.60218 14.5 2 14.5H4.625C4.72446 14.5 4.81984 14.4605 4.89016 14.3902C4.96049 14.3198 5 14.2245 5 14.125V5.875C5 5.77554 4.96049 5.68016 4.89016 5.60983C4.81984 5.53951 4.72446 5.5 4.625 5.5ZM16.5097 7.45375C16.4356 7.51883 16.3751 7.59788 16.3316 7.68636C16.2882 7.77485 16.2625 7.87105 16.2563 7.96944C16.25 8.06783 16.2632 8.1665 16.2951 8.25979C16.327 8.35308 16.377 8.43917 16.4422 8.51312C16.8039 8.92397 17.0035 9.45259 17.0035 10C17.0035 10.5474 16.8039 11.076 16.4422 11.4869C16.3752 11.5605 16.3236 11.6466 16.2902 11.7403C16.2569 11.8341 16.2425 11.9335 16.248 12.0328C16.2535 12.1322 16.2786 12.2294 16.322 12.319C16.3655 12.4085 16.4262 12.4885 16.5009 12.5542C16.5755 12.62 16.6625 12.6703 16.7567 12.7022C16.851 12.734 16.9506 12.7468 17.0498 12.7398C17.1491 12.7328 17.2459 12.7061 17.3347 12.6613C17.4236 12.6164 17.5026 12.5544 17.5672 12.4787C18.1704 11.7939 18.5032 10.9126 18.5032 10C18.5032 9.08737 18.1704 8.20607 17.5672 7.52125C17.5021 7.44705 17.423 7.38643 17.3344 7.34286C17.2458 7.2993 17.1495 7.27364 17.051 7.26737C16.9525 7.2611 16.8538 7.27434 16.7604 7.30632C16.667 7.3383 16.5809 7.3884 16.5069 7.45375H16.5097ZM20.3431 5.00031C20.2786 4.92318 20.1992 4.85981 20.1097 4.81398C20.0202 4.76815 19.9223 4.74079 19.822 4.73353C19.7217 4.72627 19.621 4.73926 19.5258 4.77173C19.4306 4.8042 19.3429 4.85548 19.2679 4.92252C19.193 4.98956 19.1323 5.071 19.0894 5.16198C19.0465 5.25297 19.0224 5.35164 19.0185 5.45214C19.0145 5.55263 19.0308 5.65289 19.0664 5.74696C19.102 5.84102 19.1561 5.92697 19.2256 5.99969C20.2097 7.09977 20.7538 8.52399 20.7538 10C20.7538 11.476 20.2097 12.9002 19.2256 14.0003C19.1561 14.073 19.102 14.159 19.0664 14.253C19.0308 14.3471 19.0145 14.4474 19.0185 14.5479C19.0224 14.6484 19.0465 14.747 19.0894 14.838C19.1323 14.929 19.193 15.0104 19.2679 15.0775C19.3429 15.1445 19.4306 15.1958 19.5258 15.2283C19.621 15.2607 19.7217 15.2737 19.822 15.2665C19.9223 15.2592 20.0202 15.2318 20.1097 15.186C20.1992 15.1402 20.2786 15.0768 20.3431 14.9997C21.5728 13.6247 22.2527 11.8447 22.2527 10C22.2527 8.15531 21.5728 6.37533 20.3431 5.00031Z"
        fill="#C7AD7A"
      />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.5 5H3C2.65482 5 2.375 5.27982 2.375 5.625V14.375C2.375 14.7202 2.65482 15 3 15H15.5C15.8452 15 16.125 14.7202 16.125 14.375V5.625C16.125 5.27982 15.8452 5 15.5 5Z"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.125 8.75L19.875 6.25V13.75L16.125 11.25"
        stroke="#C7AD7A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CameraDisableIcon() {
  return (
    <svg
      width="33"
      height="32"
      viewBox="0 0 33 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M25.5 14L31.5 10V22L25.5 18"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 5L26.5 27"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.6328 8H24.5003C24.7655 8 25.0199 8.10536 25.2074 8.29289C25.395 8.48043 25.5003 8.73478 25.5003 9V19.9538"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M23.7725 24H4.5C4.23478 24 3.98043 23.8946 3.79289 23.7071C3.60536 23.5196 3.5 23.2652 3.5 23V9C3.5 8.73478 3.60536 8.48043 3.79289 8.29289C3.98043 8.10536 4.23478 8 4.5 8H9.2275"
        stroke="#ECEFF3"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
